import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useState, useEffect, useRef } from "react";
import { Loader2, ChevronLeft, ChevronRight, Download, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import * as pdfjsLib from "pdfjs-dist";

// Configure PDF.js worker using local module
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.mjs",
  import.meta.url
).toString();

interface DocumentPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fileUrl: string;
  fileName: string;
  fileType: string;
}

export const DocumentPreviewDialog = ({
  open,
  onOpenChange,
  fileUrl,
  fileName,
  fileType,
}: DocumentPreviewDialogProps) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const isPdf = fileType?.toLowerCase().includes('pdf');
  const isWord = fileType?.toLowerCase().includes('word') || 
                 fileType?.toLowerCase().includes('document') ||
                 fileName?.toLowerCase().endsWith('.docx') ||
                 fileName?.toLowerCase().endsWith('.doc');

  useEffect(() => {
    if (open && isPdf && fileUrl) {
      setLoading(true);
      setError(null);
      const loadPdf = async () => {
        try {
          const loadingTask = pdfjsLib.getDocument(fileUrl);
          const pdf = await loadingTask.promise;
          setPdfDoc(pdf);
          setTotalPages(pdf.numPages);
          setCurrentPage(1);
          setLoading(false);
        } catch (err) {
          console.error("Error loading PDF:", err);
          setError("Unable to preview PDF. Please use the download button.");
          setLoading(false);
        }
      };
      loadPdf();
    }
  }, [open, isPdf, fileUrl]);

  useEffect(() => {
    if (pdfDoc && canvasRef.current) {
      const renderPage = async () => {
        try {
          const page = await pdfDoc.getPage(currentPage);
          const canvas = canvasRef.current;
          if (!canvas) return;

          const context = canvas.getContext("2d");
          if (!context) return;

          const viewport = page.getViewport({ scale: 1.5 });
          canvas.height = viewport.height;
          canvas.width = viewport.width;

          const renderContext = {
            canvasContext: context,
            viewport: viewport,
          };
          await page.render(renderContext as any).promise;
        } catch (err) {
          console.error("Error rendering page:", err);
        }
      };
      renderPage();
    }
  }, [pdfDoc, currentPage]);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
      // Fallback to window.open if fetch fails
      window.open(fileUrl, '_blank');
    }
  };

  const getViewerUrl = () => {
    if (isWord) {
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;
    }
    return fileUrl;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{fileName}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 relative flex flex-col">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}
          {isPdf ? (
            <>
              {error ? (
                <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center">
                  <AlertCircle className="h-12 w-12 text-muted-foreground" />
                  <p className="text-muted-foreground">{error}</p>
                  <Button onClick={handleDownload}>
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF Instead
                  </Button>
                </div>
              ) : (
                <>
                  <div className="flex-1 overflow-auto flex items-center justify-center bg-muted/20 rounded-lg">
                    <canvas ref={canvasRef} className="max-w-full h-auto" />
                  </div>
                  {pdfDoc && (
                    <div className="flex items-center justify-center gap-4 mt-4 pb-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePrevPage}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-sm">
                        Page {currentPage} of {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <iframe
              src={getViewerUrl()}
              className="w-full h-full border-0 rounded-lg"
              title={fileName}
              onLoad={() => setLoading(false)}
              onError={() => setLoading(false)}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
